/**
 * SourceFreshnessBadge — subtle "Updated N min ago" indicator surfaced
 * under the What's New / Roadmap / Service detail headers.
 *
 * Reads `sourceFreshness` produced by `useEcosystemDataset` (one entry per
 * connected service) and renders:
 *   • A small dot (green = fresh, amber = >60 min old, gray = unknown)
 *   • A "Updated 4 min ago" / "4分前に更新" relative-time string keyed off
 *     the newest successful sync timestamp across all services
 *   • A tooltip AND a screen-reader-accessible description (not just a
 *     hover-only `title`) listing the per-service freshness, each with
 *     both a relative phrase and the full JST date/time, for full
 *     transparency without requiring a mouse.
 *
 * Owner directive: never surface raw HTTP errors, internal job IDs, or
 * infrastructure detail. If a source is stale we say so quietly; we
 * don't crash the UI or show error blocks. Every timestamp rendered
 * here comes straight from the backend's own recorded sync time
 * (`lastSuccessfulSyncAtJst`) — never the browser's load time.
 */
import { useMemo } from "react";
import { CircleDot, RefreshCw } from "lucide-react";

const SERVICE_LABELS = {
  japanetccard: "JapanETCcard.com",
  japantollreceipts: "JapanTollReceipts.jp",
  japanchangemoney: "JapanChangeMoney.com",
  "nojo-enterprise": "NoJo Enterprise",
};

function relativeMinutes(isoTs, now = Date.now()) {
  if (!isoTs) return null;
  const t = new Date(isoTs).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = now - t;
  // A sync timestamp in the future (clock skew, bad upstream data) is
  // never trustworthy to present as "just now" / fresh — treat it the
  // same as missing/malformed so the caller drops it silently instead
  // of fabricating a plausible-looking age.
  if (diffMs < 0) return null;
  return Math.floor(diffMs / 60000);
}

function formatRelative(mins, isJa) {
  if (mins === null || mins === undefined) {
    return isJa ? "最終確認 不明" : "Last verified unknown";
  }
  if (mins < 1) return isJa ? "たった今確認" : "Last verified just now";
  if (mins === 1) return isJa ? "1分前に確認" : "Last verified 1 min ago";
  if (mins < 60) return isJa ? `${mins}分前に確認` : `Last verified ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return isJa ? "1時間前に確認" : "Last verified 1 hr ago";
  if (hrs < 24) return isJa ? `${hrs}時間前に確認` : `Last verified ${hrs} hrs ago`;
  const days = Math.floor(hrs / 24);
  return isJa ? `${days}日前に確認` : `Last verified ${days} d ago`;
}

// Absolute JST date+time, for the accessible/tooltip description only —
// the visible badge itself stays relative (small + unobtrusive).
function formatAbsoluteJst(isoTs, isJa) {
  if (!isoTs) return null;
  const d = new Date(isoTs);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleString(isJa ? "ja-JP" : "en-US", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return null;
  }
}

export default function SourceFreshnessBadge({
  sourceFreshness,
  lang = "en",
  className = "",
}) {
  const isJa = lang === "ja";
  const summary = useMemo(() => {
    if (!Array.isArray(sourceFreshness) || sourceFreshness.length === 0) {
      return { mins: null, anyStale: false, perService: [] };
    }
    let newest = null;
    let anyStale = false;
    // Missing / null / empty / malformed / future timestamps resolve to
    // `null` from relativeMinutes — drop those entries entirely rather
    // than render a misleading "unknown" line (owner directive: hide
    // cleanly, never show Invalid Date/placeholder text).
    const perService = sourceFreshness
      .map((s) => {
        const m = relativeMinutes(s.lastSuccessfulSyncAtJst);
        if (m === null) return null;
        if (newest === null || m < newest) newest = m;
        if (s.stale) anyStale = true;
        return {
          serviceId: s.serviceId,
          label: SERVICE_LABELS[s.serviceId] || s.serviceId,
          mins: m,
          stale: !!s.stale,
          absolute: formatAbsoluteJst(s.lastSuccessfulSyncAtJst, isJa),
        };
      })
      .filter(Boolean);
    return { mins: newest, anyStale, perService };
  }, [sourceFreshness, isJa]);

  // Nothing reliable to show — render nothing rather than a misleading
  // "unknown"/blank badge (directive: never show a fabricated signal).
  if (summary.perService.length === 0) return null;

  const dotColor = summary.anyStale
    ? "#D97706" // amber-600
    : summary.mins === null
    ? "#94A3B8" // slate-400
    : "#16A34A"; // green-600

  const perServiceLines = summary.perService.map(
    (s) =>
      `${s.label}: ${formatRelative(s.mins, isJa)}${
        s.absolute ? ` (${s.absolute})` : ""
      }${s.stale ? (isJa ? " — 古い" : " — stale") : ""}`
  );
  const title = perServiceLines.join("\n");
  const accessibleLabel = `${
    isJa ? "データ同期状況。" : "Data synchronization status."
  } ${perServiceLines.join(". ")}`;

  return (
    <span
      data-testid="source-freshness-badge"
      title={title}
      role="note"
      aria-label={accessibleLabel}
      tabIndex={0}
      className={[
        "inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.04em] font-semibold text-slate-500 select-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#663399]/40 focus-visible:rounded-full",
        className,
      ].join(" ")}
    >
      <CircleDot
        className="h-3 w-3 shrink-0"
        strokeWidth={2.5}
        color={dotColor}
        aria-hidden
      />
      <RefreshCw className="h-2.5 w-2.5 opacity-70 shrink-0" strokeWidth={2.25} aria-hidden />
      <span aria-hidden="true">{formatRelative(summary.mins, isJa)}</span>
      {summary.anyStale && (
        <span
          className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9.5px] tracking-[0.08em] uppercase font-bold"
          data-testid="source-freshness-stale"
          aria-hidden="true"
        >
          {isJa ? "古い" : "Stale"}
        </span>
      )}
    </span>
  );
}
